import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import ListView from './AvailableSlots/ListView';
import TableView from './AvailableSlots/TableView';

interface TimeSlotSelectionProps {
  selectedSlot: string | null;
  onSlotSelect: (slotId: string) => void;
  slotsByDate?: Record<string, Record<string, string[]>>;
  onPrevWeek?: () => void;
  onNextWeek?: () => void;
  currentStart: Date;
  onOperatoriesSelect?: (operatories: string[]) => void;
  daysToShow?: number;
  viewMode?: 'table' | 'list';
  isPrevDisabled?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  searchLimitReached?: boolean;
  visibleDates?: Date[]; // Allow override
}

const TimeSlotSelection = ({
  selectedSlot,
  onSlotSelect,
  slotsByDate,
  onPrevWeek,
  onNextWeek,
  currentStart,
  onOperatoriesSelect,
  daysToShow = 7,
  viewMode,
  isPrevDisabled = false,
  onLoadMore,
  isLoadingMore,
  searchLimitReached,
  visibleDates: visibleDatesOverride,
}: TimeSlotSelectionProps) => {
  const { treatment } = useSelector((s: RootState) => s.appointment);

  const getVisibleDates = () => {
    if (visibleDatesOverride) return visibleDatesOverride;

    const dates: Date[] = [];
    for (let i = 0; i < daysToShow; i++) {
      const d = new Date(currentStart);
      d.setDate(currentStart.getDate() + i);
      dates.push(d);
    }
    return dates;
  };
  const visibleDates = getVisibleDates();

  const formatDate = (date: Date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const bufferMinutes = treatment?.bufferMinutes || 0;

  const commonProps = {
    visibleDates,
    slotsByDate,
    selectedSlot,
    onSlotSelect,
    onOperatoriesSelect,
    bufferMinutes,
  };

  const dateRangeLabel = visibleDates.length > 0 && `${formatDate(visibleDates[0])} - ${formatDate(visibleDates[visibleDates.length - 1])}`

  return (
    <div className="space-y-4">
      <div className="">
        {/* Sticky Header - Same for both views */}
        <div className={`flex items-center ${viewMode === 'list' ? 'justify-center' : 'justify-between'} mb-4`}>
          {viewMode !== 'list' && (
            <button
              onClick={onPrevWeek}
              disabled={isPrevDisabled}
              className="flex items-center px-3 py-1 text-sm text-gray-600 hover:text-teamcare-yellow disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft />
            </button>
          )}

          <h3 className="text-xs sm:text-sm font-semibold text-gray-900">
            {dateRangeLabel}
          </h3>

          {viewMode !== 'list' && (
            <button
              onClick={onNextWeek}
              className="flex items-center px-3 py-1 text-sm text-gray-600 hover:text-teamcare-yellow disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight />
            </button>
          )}
        </div>

        {/* Table View */}
        {viewMode === 'table' && <TableView {...commonProps} daysToShow={daysToShow} />}

        {/* List View */}
        {viewMode === 'list' && (
          <ListView
            {...commonProps}
            onLoadMore={onLoadMore}
            isLoadingMore={isLoadingMore}
            searchLimitReached={searchLimitReached}
          />
        )}
      </div>
    </div>
  );
};

export default TimeSlotSelection;